import { AppEnum, DetailItem, DetailSection } from "@/Shared/Types";
import { AppEnumHelper, DateFormatHelper, formatName, SoNumber } from "@/Shared/Helpers";
import { AuditViewModel } from "../../../../Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "../../../../Shared/Logic/Models/AuditModel";
import { PersonModel } from "../PersonModel";

/**
 * Class: PersonViewModel
 *
 * Description:
 * ViewModel for displaying person information in the UI.
 * Handles formatting of names, phones, addresses, and audit info.
 * Provides sections for detail display and utility methods for phone and full name.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class PersonViewModel {

    public id!: number;
    public firstname!: string;
    public lastname!: string;
    public fullName!: string;
    public civility!: string;
    public phone!: string;
    public email!: string;
    public profession!: string;
    public birthDate!: string;
    public address!: string;
    public phone1!: string;
    public phone2!: string;
    public eitherPhone!: string;
    public creationDate!: string;
    public cardHeaderTitle!: string;
    public cardHeaderPhoneLink! : string;
    public audit!: AuditViewModel;

    public country! : string;
    public city! : string;

    protected UserPositionEnum!: AppEnum[];

     /**
     * =====================================================
     * Constructor
     * =====================================================
     *
     * Initializes the ViewModel from a PersonModel and enums.
     * Populates all display fields and prepares audit information.
     *
     * @param person - PersonModel instance
     * @param personViewEnums - Enum definitions for dropdowns/labels
     */
    public constructor(
        protected person: PersonModel, 
        protected civilityEnum: AppEnum[]) {
            this.initialize();

    }

    /**
     * =====================================================
     * Initialization
     * =====================================================
     *
     * Sets up all display fields, formats phone numbers, dates,
     *  audit info.
     */
    private initialize() {

        this.audit = new AuditViewModel(this.person as AuditModel);

        this.id = this.person?.id || 0;
        this.civility = AppEnumHelper.getLabel(this.civilityEnum, this.person?.civility);

        this.lastname = this.person?.lastname?.toUpperCase() || "";

        this.firstname = this.person?.firstname || "";

        this.fullName = formatName(this.person);

        this.profession = this.person?.profession ?? "";

        this.birthDate = DateFormatHelper.toLocaleDateString(this.person?.birthDate);

        this.address = this.person?.address ?? "";

        this.phone1 = SoNumber.phone(this.person?.phone1);

        this.phone2 = SoNumber.phone(this.person?.phone2);

        this.eitherPhone = this.phone1 ?? this.phone2

        this.email = this.person?.email ?? "";

        this.creationDate = DateFormatHelper.toLocaleDateString(this.person?.createdAt);

        this.city = this.person?.city ?? "";
        this.country = this.person?.country ?? "";

        this.phone = (this.person?.phone1 && this.person?.phone2) 
        ? this.phone1 + ' / ' + this.phone2 
        : this.phone1 + this.phone2;

          
        this.cardHeaderTitle = this.fullName;
        this.cardHeaderPhoneLink = this.eitherPhone;
    }

    /**
     * =====================================================
     * Phone Utilities
     * =====================================================
     */
    hasPhone = () => !!(this.phone1.length > 0 || this.phone2.length > 0);

    getPhone = () => {

        let phone1 = this.phone1;
        let phone2 = this.phone2;

        if (phone1 && phone2) {
            return phone1 + " / " + phone2;
        }
        return phone1 || phone2 || "";
    }

     /**
     * =====================================================
     * Sections for UI Display
     * =====================================================
     */
    get civilitySection() {
        return [
            { label: "Nom", value: this.lastname },
            { label: "Prénom", value: this.firstname },
            { label: "Civilité", value: this.civility },
            { label: "Date de Naissance", value: this.birthDate }
        ] as DetailItem[];
    }

    get localisationSection() {
        return [
            { label: "Adresse", value: this.address },
            { label: "Ville", value: this.city },
            { label: "Secteur", value: this.country }
        ] as DetailItem[];
    }

    get coordinatesSection() {
        return [
            { label: "Téléphone 1", value: this.phone1 },
            { label: "Téléphone 2", value: this.phone2 },
            { label: "E-mail", value: this.email }
        ] as DetailItem[];
    }

    /**
     * =====================================================
     * All Sections Aggregator
     * =====================================================
     *
     * Returns all sections including audit trail, for UI display.
     */
    getAllSections = (): DetailSection[] => {
        return [
            {
                sectionFn: () => this.civilitySection,
                legend: 'Civilité',
                type: 'box'
            },
            {
                sectionFn: () => this.localisationSection,
                legend: 'Localisation',
                type: 'box'
            },
            {
                sectionFn: () => this.coordinatesSection,
                legend: 'Coordonnées',
                type: 'box'
            },
            {
                sectionFn: this.audit.getAuditTrail,
                legend: 'Traçabilité',
                type: 'box'
            },
        ]
    }

    /**
     * =====================================================
     * Title
     * =====================================================
     *
     * Returns a dynamic title for UI cards or headers
     *
     * @param startStr - Optional prefix for the title
     */
    public getTitle(startStr? : string) : string {
        return (startStr ?? "Personne") + " N° " + this.id + ' - ' + this.lastname?.toLocaleUpperCase();
    }
}